import React,{useState,useMemo,useCallback,useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import {Modal,Badge,EmptyState} from '../ui/SharedUI';
import PropertyDocumentActions from './PropertyDocumentActions';
import SafeIcon from '../../common/SafeIcon';
import {useFeedback} from '../ui/FeedbackUI';
import {demoRepository} from '../../repository/demoRepository';
import {calculations} from '../../utils/calculations';
import SmartDateInput from '../ui/SmartDateInput';
import {DOCUMENT_TYPES,SAFE_DOCUMENT_TYPES,getDocumentTypeLabel,getEffectiveDocumentType,} from '../../config/documentTypes';

const LIFECYCLE_RANK={entry_condition_report: 1,inspection_report: 2,exit_condition_report: 3,};
const LIFECYCLE_TYPES=['entry_condition_report','inspection_report','exit_condition_report'];

const toTimestamp=value=> {
  if (!value) return 0;
  if (typeof value==='number') return value;
  const timestamp=new Date(value).getTime();
  return Number.isFinite(timestamp) ? timestamp : 0;
};

const TenantDocumentsPanel=({tenant,lease,property,space,onChanged})=> {
const {showToast,showSyncProgress}=useFeedback();
const navigate=useNavigate();
const [searchTerm,setSearchTerm]=useState('');
const [typeFilter,setTypeFilter]=useState('All');
const [sortOrder,setSortOrder]=useState('newest');
const [isUploadModalOpen,setIsUploadModalOpen]=useState(false);
const [isSubmitting,setIsSubmitting]=useState(false);
const [selectedFile,setSelectedFile]=useState(null);
const [dataVersion,setDataVersion]=useState(0);
const [formData,setFormData]=useState({title: '',documentType: 'lease_agreement',documentDate: new Date().toISOString().split('T')[0],inspectionId: '',notes: '',});

useEffect(()=> {
const handleRefresh=()=> setDataVersion(prev=> prev + 1);
window.addEventListener('mpp:data-refreshed',handleRefresh);
return ()=> window.removeEventListener('mpp:data-refreshed',handleRefresh);
},[]);

const allDocuments=useMemo(()=> [...demoRepository.getDocuments()],[tenant?.id,lease?.id,dataVersion]);
const allInspections=useMemo(()=> [...demoRepository.getInspections()],[tenant?.id,lease?.id,dataVersion]);

const tenantInspections=useMemo(()=> {
if (!tenant) return [];
return allInspections.filter((insp)=> {
if (insp.tenantId===tenant.id) return true;
if (lease && insp.leaseId===lease.id) return true;
return false;
});
},[allInspections,tenant,lease]);

const tenantInspectionIds=useMemo(
()=> new Set(tenantInspections.map((i)=> i.id)),
[tenantInspections]
);

const tenantDocuments=useMemo(()=> {
if (!tenant) return [];
const seen=new Set();
const results=[];
for (const doc of allDocuments) {
let isRelated=false;
if (doc.tenantId===tenant.id) isRelated=true;
if (lease && doc.leaseId===lease.id) isRelated=true;
if (doc.inspectionId && tenantInspectionIds.has(doc.inspectionId)) isRelated=true;
if (isRelated && !seen.has(doc.id)) {
seen.add(doc.id);
results.push(doc);
}
}
return results;
},[allDocuments,tenant,lease,tenantInspectionIds]);

const filteredDocuments=useMemo(()=> {
const searchTokens=searchTerm ? searchTerm.toLowerCase().split(/\s+/).filter(Boolean) : [];
let docs=tenantDocuments.filter((doc)=> {
if (typeFilter !=='All' && getEffectiveDocumentType(doc) !==typeFilter) return false;
if (searchTokens.length===0) return true;
const haystack=[
doc.title,
doc.fileName,
getEffectiveDocumentType(doc),
getDocumentTypeLabel(doc),
doc.notes,
doc.description,
calculations.formatDateAU(doc.documentDate),
calculations.formatDateAU(doc.uploadedDate),
]
.filter(Boolean)
.join(' ')
.toLowerCase();
return searchTokens.every((token)=> haystack.includes(token));
});
docs.sort((a,b)=> {
const dateA=toTimestamp(a.documentDate) || toTimestamp(a.uploadedDate) || toTimestamp(a.created) || 0;
const dateB=toTimestamp(b.documentDate) || toTimestamp(b.uploadedDate) || toTimestamp(b.created) || 0;
if (sortOrder==='oldest') return dateA - dateB;
return dateB - dateA;
});
return docs;
},[tenantDocuments,searchTerm,typeFilter,sortOrder]);

const lifecycleDocuments=useMemo(()=> {
return tenantDocuments
.filter((doc)=> LIFECYCLE_TYPES.includes(getEffectiveDocumentType(doc)))
.sort((a,b)=> {
const dateA=toTimestamp(a.documentDate) || toTimestamp(a.uploadedDate) || 0;
const dateB=toTimestamp(b.documentDate) || toTimestamp(b.uploadedDate) || 0;
if (dateA !==dateB) return dateA - dateB;
const rankA=LIFECYCLE_RANK[getEffectiveDocumentType(a)] || 99;
const rankB=LIFECYCLE_RANK[getEffectiveDocumentType(b)] || 99;
return rankA - rankB;
});
},[tenantDocuments]);

const exitBeforeEntryWarning=useMemo(()=> {
const entryDoc=lifecycleDocuments.find(
(d)=> getEffectiveDocumentType(d)==='entry_condition_report'
);
const exitDoc=lifecycleDocuments.find(
(d)=> getEffectiveDocumentType(d)==='exit_condition_report'
);
if (entryDoc && exitDoc) {
const entryDate=toTimestamp(entryDoc.documentDate) || toTimestamp(entryDoc.uploadedDate) || 0;
const exitDate=toTimestamp(exitDoc.documentDate) || toTimestamp(exitDoc.uploadedDate) || 0;
if (exitDate > 0 && entryDate > 0 && exitDate < entryDate) {
return true;
}
}
return false;
},[lifecycleDocuments]);

const getOpenUrl=useCallback((doc)=> {
const fileNames=Array.isArray(doc.files) && doc.files.length > 0 ? doc.files.filter(Boolean) : Array.isArray(doc.fileNames) && doc.fileNames.length > 0 ? doc.fileNames.filter(Boolean) : doc.fileName ? [doc.fileName] : [];
const baseUrl=(import.meta.env.VITE_POCKETBASE_URL || '').trim().replace(/\/$/,'');
const mappedUrl=Array.isArray(doc.fileUrls) ? doc.fileUrls.find((url)=> typeof url==='string' && url.length > 0) : '';
if (mappedUrl && mappedUrl.startsWith('http')) return mappedUrl;
if (mappedUrl && mappedUrl.startsWith('/') && baseUrl) return `${baseUrl}${mappedUrl}`;
if (baseUrl && doc.id && fileNames.length > 0) {
return `${baseUrl}/api/files/documents/${doc.id}/${encodeURIComponent(String(fileNames[0]))}`;
}
return '';
},[]);

const handleFileSelect=(e)=> {
const file=e.target.files?.[0];
if (!file) return;
const allowedExtensions=['pdf','docx','zip','png','jpg','jpeg'];
const allowedTypes=[
'application/pdf',
'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
'application/zip',
'application/x-zip-compressed',
'multipart/x-zip',
'image/png',
'image/jpeg',
'image/jpg',
];
const extension=String(file.name || '').split('.').pop()?.toLowerCase();
const isAllowed=allowedTypes.includes(file.type) || allowedExtensions.includes(extension) || (file.type==='' && allowedExtensions.includes(extension));
const isSizeOk=file.size <=15 * 1024 * 1024;
if (!isAllowed) {
showToast('Only PDF,DOCX,ZIP,PNG,JPG or JPEG files are allowed.','error');
e.target.value='';
return;
}
if (!isSizeOk) {
showToast('File exceeds the 15MB size limit.','error');
e.target.value='';
return;
}
setSelectedFile(file);
if (!formData.title) {
const baseName=file.name.replace(/\.[^/.]+$/,'');
setFormData((prev)=> ({...prev,title: baseName}));
}
};

const handleRemoveFile=()=> {
setSelectedFile(null);
};

const handleSubmit=async (e)=> {
e.preventDefault();
if (!selectedFile) {
showToast('Please select a file to upload.','error');
return;
}
setIsSubmitting(true);
try {
const isSafe=SAFE_DOCUMENT_TYPES.includes(formData.documentType);
const finalDocumentType=isSafe ? formData.documentType : 'other';
const intendedDocumentType=isSafe ? null : formData.documentType;
const docDate=new Date(formData.documentDate).getTime();
const payload={
title: formData.title || selectedFile.name.replace(/\.[^/.]+$/,''),
documentType: finalDocumentType,
documentDate: docDate,
notes: formData.notes || '',
status: 'active',
files: [selectedFile],
fileName: selectedFile.name,
fileSize: selectedFile.size,
fileType: selectedFile.type || selectedFile.name?.split('.').pop()?.toUpperCase() || '',
uploadedDate: Date.now(),
tenantId: tenant.id,
propertyId: property?.id || lease?.propertyId || '',
leaseId: lease?.id || '',
spaceId: space?.id || lease?.spaceId || '',
inspectionId: formData.inspectionId || '',
metadata: {
intendedDocumentType,
uploadedFrom: 'TenantDetailPage',
tenantId: tenant.id,
leaseId: lease?.id || '',
propertyId: property?.id || lease?.propertyId || '',
spaceId: space?.id || lease?.spaceId || '',
inspectionId: formData.inspectionId || '',
fileName: selectedFile.name,
fileSize: selectedFile.size,
fileType: selectedFile.type || selectedFile.name?.split('.').pop()?.toUpperCase() || '',
},
};
const result=await showSyncProgress({
title: 'Uploading Tenant Document',
stages: [
'Preparing document details',
'Uploading file to PocketBase',
'Saving document record',
'Updating document list',
],
action: async ()=> {
const docResult=await demoRepository.addDocument(payload);
if (!docResult?.success) {
throw new Error(docResult?.error || 'Document save failed.');
}
return {success: true,record: docResult.record};
},
successMessage: 'Tenant document uploaded successfully.',
errorMessage: 'Document save failed.',
});
if (result?.success) {
showToast('Tenant document uploaded successfully.','success');
setIsUploadModalOpen(false);
setSelectedFile(null);
setFormData({
title: '',
documentType: 'lease_agreement',
documentDate: new Date().toISOString().split('T')[0],
inspectionId: '',
notes: '',
});
setDataVersion(prev=> prev + 1);
if (typeof onChanged==='function') onChanged();
}
} catch (error) {
showToast(error?.message || 'Document save failed.','error');
} finally {
setIsSubmitting(false);
}
};

const openUploadModal=()=> {
setSelectedFile(null);
setFormData({
title: '',
documentType: 'lease_agreement',
documentDate: new Date().toISOString().split('T')[0],
inspectionId: '',
notes: '',
});
setIsUploadModalOpen(true);
};

const inputClass='w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white';
const labelClass='block text-xs font-medium text-slate-600 mb-1';

return (
<div className="space-y-6 animate-fade-in">
<div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
<div>
<h3 className="text-lg font-semibold text-slate-800">Documents & Inspections</h3>
<p className="text-sm text-slate-500 mt-1">
Documents linked to this tenant,lease,and related inspections.
</p>
</div>
<button
type="button"
onClick={openUploadModal}
className="px-3 py-1.5 bg-slate-900 text-white text-sm rounded-lg hover:bg-slate-800 flex items-center gap-1.5 whitespace-nowrap"
>
<SafeIcon icon="UploadCloud" className="text-base" />
Add Tenant Document
</button>
</div>

{exitBeforeEntryWarning && (
<div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
<SafeIcon icon="AlertCircle" className="text-amber-600 text-lg shrink-0 mt-0.5" />
<p className="text-sm text-amber-800">
Exit report is dated before the entry report. Please check the document dates.
</p>
</div>
)}

<div className="flex flex-col sm:flex-row gap-3">
<div className="relative flex-1">
<SafeIcon icon="Search" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
<input
type="text"
placeholder="Search tenant documents..."
value={searchTerm}
onChange={(e)=> setSearchTerm(e.target.value)}
className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white"
/>
</div>
<select
value={typeFilter}
onChange={(e)=> setTypeFilter(e.target.value)}
className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-amber-500"
>
<option value="All">All Types</option>
{DOCUMENT_TYPES.map((type)=> (
<option key={type.value} value={type.value}>
{type.label}
</option>
))}
</select>
<select
value={sortOrder}
onChange={(e)=> setSortOrder(e.target.value)}
className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-amber-500"
>
<option value="newest">Newest First</option>
<option value="oldest">Oldest First</option>
</select>
</div>

{filteredDocuments.length > 0 ? (
<div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
<div className="overflow-x-auto">
<table className="w-full text-left border-collapse min-w-max">
<thead>
<tr className="bg-slate-50 border-b border-slate-200">
<th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Title</th>
<th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Type</th>
<th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Date</th>
<th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Property</th>
<th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Lease</th>
<th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Inspection</th>
<th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">File</th>
<th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Status</th>
<th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider sticky right-0 z-20 min-w-[170px] bg-slate-50 border-l border-slate-200">Actions</th>
</tr>
</thead>
<tbody className="divide-y divide-slate-100">
{filteredDocuments.map((doc)=> {
const fileNames=Array.isArray(doc.files) && doc.files.length > 0 ? doc.files.filter(Boolean) : Array.isArray(doc.fileNames) && doc.fileNames.length > 0 ? doc.fileNames.filter(Boolean) : doc.fileName ? [doc.fileName] : [];
const fileCount=doc.fileCount || fileNames.length || 0;
const openUrl=getOpenUrl(doc);
const linkedInspection=tenantInspections.find(
(i)=> i.id===doc.inspectionId
);
const linkedProperty=doc.propertyId ? demoRepository.getPropertyById(doc.propertyId) : property;
const linkedLease=doc.leaseId ? demoRepository.getLeaseById(doc.leaseId) : lease;
return (
<tr key={doc.id} className="hover:bg-slate-50/50 transition-colors">
<td className="px-4 py-3 text-sm">
<div className="font-medium text-slate-900 truncate max-w-[200px]">
{doc.title || 'Untitled'}
</div>
{doc.notes && (
<div className="text-xs text-slate-500 truncate max-w-[200px]">
{doc.notes}
</div>
)}
</td>
<td className="px-4 py-3 text-sm">
<Badge label={getDocumentTypeLabel(doc)} status="default" />
</td>
<td className="px-4 py-3 text-sm text-slate-700">
{calculations.formatDateAU(doc.documentDate || doc.uploadedDate)}
</td>
<td className="px-4 py-3 text-sm text-slate-700">
{linkedProperty?.name || '-'}
</td>
<td className="px-4 py-3 text-sm text-slate-700">
{linkedLease ? `${calculations.formatDateAU(linkedLease.startDate)} - ${calculations.formatDateAU(linkedLease.endDate)}` : '-'}
</td>
<td className="px-4 py-3 text-sm text-slate-700">
{linkedInspection ? `${linkedInspection.type || 'Inspection'}` : '-'}
</td>
<td className="px-4 py-3 text-sm text-slate-500">
{fileCount > 0 ? `${fileCount} file(s)` : 'Metadata only'}
</td>
<td className="px-4 py-3 text-sm">
<Badge label={doc.status==='active' ? 'Active' : doc.status} status={doc.status==='active' ? 'active' : 'default'} />
</td>
<td className="px-4 py-3 sticky right-0 z-10 min-w-[170px] bg-white border-l border-slate-100">
<PropertyDocumentActions
doc={doc}
openUrl={openUrl}
onEdit={()=> showToast('Edit document details will open here next.','info')}
onViewInDocuments={()=> navigate('/documents')}
/>
</td>
</tr>
);
})}
</tbody>
</table>
</div>
</div>
) : (
<EmptyState
title="No Tenant Documents"
description="Upload documents directly linked to this tenant,lease,or related inspections."
actionLabel="Add Tenant Document"
onAction={openUploadModal}
icon="FileText"
/>
)}

<Modal
isOpen={isUploadModalOpen}
onClose={()=> !isSubmitting && setIsUploadModalOpen(false)}
title="Add Tenant Document"
>
<form onSubmit={handleSubmit} className="space-y-4">
<div>
<label className={labelClass}>Document Title</label>
<input
type="text"
value={formData.title}
onChange={(e)=> setFormData({...formData,title: e.target.value})}
className={inputClass}
placeholder="e.g. Lease Agreement 2024"
/>
<p className="text-xs text-slate-400 mt-1">
Leave blank to auto-derive from filename.
</p>
</div>
<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
<div>
<label className={labelClass}>Document Type *</label>
<select
value={formData.documentType}
onChange={(e)=> setFormData({...formData,documentType: e.target.value})}
className={inputClass}
required
>
{DOCUMENT_TYPES.map((type)=> (
<option key={type.value} value={type.value}>
{type.label}
</option>
))}
</select>
</div>
<div>
<label className={labelClass}>Document Date</label>
<SmartDateInput
value={formData.documentDate}
onChange={(e)=> setFormData({...formData,documentDate: e.target.value})}
className={inputClass}
/>
</div>
</div>
<div>
<label className={labelClass}>Related Inspection (Optional)</label>
<select
value={formData.inspectionId}
onChange={(e)=> setFormData({...formData,inspectionId: e.target.value})}
className={inputClass}
>
<option value="">No inspection linked</option>
{tenantInspections.map((insp)=> (
<option key={insp.id} value={insp.id}>
{insp.type || 'Inspection'} — {calculations.formatDateAU(insp.inspectionDate)}
</option>
))}
</select>
</div>
<div>
<label className={labelClass}>Notes</label>
<textarea
value={formData.notes}
onChange={(e)=> setFormData({...formData,notes: e.target.value})}
className={inputClass}
rows="3"
placeholder="Additional notes..."
/>
</div>
<div className="border-t pt-4">
<label className={labelClass}>File *</label>
{selectedFile ? (
<div className="flex items-center justify-between bg-white border border-slate-200 px-3 py-2 rounded-lg">
<div className="flex items-center gap-2 min-w-0">
<SafeIcon icon="FileText" className="text-slate-400 shrink-0" />
<div className="truncate">
<p className="text-sm font-medium text-slate-800 truncate">{selectedFile.name}</p>
<p className="text-xs text-slate-500">
{(selectedFile.size / 1024 / 1024).toFixed(2)} MB
</p>
</div>
</div>
<button
type="button"
onClick={handleRemoveFile}
className="text-red-500 hover:bg-red-50 p-1 rounded transition-colors shrink-0"
>
<SafeIcon icon="X" className="text-sm" />
</button>
</div>
) : (
<div className="relative border-2 border-dashed border-slate-200 rounded-xl p-6 bg-slate-50 hover:bg-slate-100 transition-colors cursor-pointer flex flex-col items-center justify-center text-center">
<input
type="file"
accept=".pdf,.docx,.zip,.png,.jpg,.jpeg,application/pdf,application/zip,application/x-zip-compressed,image/png,image/jpeg"
onChange={handleFileSelect}
className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
/>
<div className="w-10 h-10 bg-white rounded-full shadow-sm flex items-center justify-center mb-2">
<SafeIcon icon="UploadCloud" className="text-slate-400" />
</div>
<p className="text-xs font-medium text-slate-700">Click to select a file</p>
<p className="text-[10px] text-slate-400 mt-1">PDF,DOCX,ZIP,PNG,JPG (max 15MB)</p>
</div>
)}
</div>
<div className="flex justify-end gap-3 pt-4 border-t">
<button
type="button"
onClick={()=> setIsUploadModalOpen(false)}
className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg"
disabled={isSubmitting}
>
Cancel
</button>
<button
type="submit"
disabled={isSubmitting || !selectedFile}
className="px-4 py-2 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 disabled:opacity-50 flex items-center gap-2"
>
{isSubmitting && (
<div className="h-3 w-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
)}
{isSubmitting ? 'Uploading...' : 'Save Document'}
</button>
</div>
</form>
</Modal>
</div>
);
};

export default TenantDocumentsPanel;